import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { HardHat, Play } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Demo mode only visible with ?demo=bso secret parameter
  const showDemoMode = searchParams.get('demo') === 'bso';

  useEffect(() => {
    // Pre-fill email if redirected from signup
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.justSignedUp) {
      toast.success('Account created! Please sign in.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (role) => {
    setEmail(`${role}@demo.com`);
    setPassword('demo123');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_jobcosting-mvp/artifacts/g5eosz0s_BSO%20FS%20LOGO.PNG" 
              alt="BSO Financial Services" 
              className="h-20 mx-auto mb-6"
            />
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">BSO SiteTrack</h2>
            <p className="mt-2 text-sm text-slate-600">Construction job costing & project management</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="mt-2 rounded-sm border-2 focus-visible:ring-[#1A3668]"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-2 rounded-sm border-2 focus-visible:ring-[#1A3668]"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#1A3668] hover:bg-[#142A52] text-white font-bold py-3 rounded-sm shadow-sm transition-all active:scale-95"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#1A3668]" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-sm text-slate-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#1A3668] font-bold hover:underline" data-testid="signup-link">
                Sign up free
              </Link>
            </p>
          </div>

          {/* Sandbox Demo Section - Only visible with ?demo=bso */}
          {showDemoMode && (
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Play className="w-4 h-4 text-blue-600" />
                <h3 className="font-bold text-blue-800">Live Demo / Sandbox</h3>
              </div>
              <p className="text-sm text-blue-700 mb-3">
                Explore BSO SiteTrack with sample data. Perfect for evaluating the platform.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  onClick={() => fillDemoCredentials('foreman')}
                  variant="outline"
                  data-testid="demo-foreman-button"
                  className="text-xs border-2 border-blue-300 bg-white hover:bg-blue-50 text-blue-700 rounded-sm"
                >
                  <HardHat className="w-3 h-3 mr-1" />
                  Foreman
                </Button>
                <Button
                  type="button"
                  onClick={() => fillDemoCredentials('office')}
                  variant="outline"
                  data-testid="demo-office-button"
                  className="text-xs border-2 border-blue-300 bg-white hover:bg-blue-50 text-blue-700 rounded-sm"
                >
                  Office
                </Button>
                <Button
                  type="button"
                  onClick={() => fillDemoCredentials('owner')}
                  variant="outline"
                  data-testid="demo-owner-button"
                  className="text-xs border-2 border-blue-300 bg-white hover:bg-blue-50 text-blue-700 rounded-sm"
                >
                  Owner
                </Button>
              </div>
              <p className="text-xs text-blue-600 mt-2 text-center">Click a role, then Sign In with password: <strong>demo123</strong></p>
            </div>
          )}
          
          {/* Footer tagline */}
          <p className="text-[10px] text-slate-400 text-center mt-6">
            Created by BSO Financial Services
          </p>
        </div>
      </div>

      {/* Right Side - Image with Overlay */}
      <div className="hidden lg:flex flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1757049610893-ea1a9be199c9?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Construction site"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A3668] opacity-80"></div>
        <div className="relative z-10 flex items-center justify-center p-12 text-white">
          <div className="max-w-lg">
            <h3 className="text-4xl font-extrabold tracking-tight mb-4">Precision Job Costing</h3>
            <p className="text-lg leading-relaxed opacity-90">
              Track labor, materials, and equipment costs in real-time. Stay ahead of budgets with detailed PO tracking and instant field-to-office workflows.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
